import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useJobs } from '../services/JobContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useFilters } from '../hooks/useFilters';
import { getJobColor, groupInterviewsByJob, filterJobs } from '../utils/helpers';
import { JOB_STATUSES } from '../constants';
import { ROLES } from '../utils/permissions';
import api from '../services/api';
import { SkeletonKanban } from './SkeletonLoader';
import JobDetails from './JobDetails';

/* -----------------------------
   Helpers
   ----------------------------- */

const StatusPill = ({ status }) => {
  const safe = (status || 'waiting').toLowerCase();
  return (
    <span className={`kanban-status-pill kanban-status-pill--${safe}`}>
      {safe.replace('_', ' ')}
    </span>
  );
};

const safeDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmt = (value, pattern) => {
  const d = safeDate(value);
  return d ? format(d, pattern) : '—';
};

/* -----------------------------
   Component
   ----------------------------- */

const Kanban = () => {
  const { jobs, interviews, updateInterview, updateJob, loading } = useJobs();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const { filters, updateFilter, clearFilters, hasActiveFilters } = useFilters();

  const [selectedInterview, setSelectedInterview] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [viewJobDetails, setViewJobDetails] = useState(null);
  const [bdOptions, setBdOptions] = useState([]);
  const [loadingBds, setLoadingBds] = useState(true);

  useEffect(() => {
    const loadBds = async () => {
      setLoadingBds(true);
      try {
        const users = await api.getUsers();
        const bds = (users || [])
          .filter((u) => u.role === ROLES.BD)
          .map((u) => u.name || u.email)
          .filter(Boolean);
        setBdOptions(Array.from(new Set(bds)));
      } catch (error) {
        console.error('Failed to load BD users', error);
        setBdOptions([]);
      } finally {
        setLoadingBds(false);
      }
    };
    loadBds();
  }, []);

  // Group interviews by job
  const interviewsByJob = useMemo(() => groupInterviewsByJob(interviews), [interviews]);

  // Calculate dynamic rounds based on data
  const dynamicRounds = useMemo(() => {
    let maxRound = 0;
    Object.values(interviewsByJob).forEach((jobInterviews) => {
      Object.values(jobInterviews).forEach((interview) => {
        maxRound = Math.max(maxRound, interview.round);
      });
    });
    if (maxRound === 0) maxRound = 5;
    return Array.from({ length: maxRound }, (_, i) => i + 1);
  }, [interviewsByJob]);

  // Filter jobs by tab and then apply other filters
  const getJobsByTab = (tab) => {
    let filteredJobs;
    switch (tab) {
      case 'successful':
        filteredJobs = jobs.filter(
          (job) => job.status === 'completed' && interviewsByJob[job.id]
        );
        break;
      case 'rejected':
        filteredJobs = jobs.filter(
          (job) => job.status === 'rejected' && interviewsByJob[job.id]
        );
        break;
      default:
        filteredJobs = jobs.filter((job) => {
          const hasInterviews = interviewsByJob[job.id];
          if (!hasInterviews) return false;
          return job.status !== 'completed' && job.status !== 'rejected';
        });
    }

    let filtered = filterJobs(filteredJobs, {
      searchTerm: filters.searchTerm,
      bdName: filters.bdName,
      status: filters.status,
    });

    filtered = filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'maxRound': {
          const jobAInterviews = interviewsByJob[a.id] || {};
          const jobBInterviews = interviewsByJob[b.id] || {};
          const maxRoundA = Math.max(0, ...Object.keys(jobAInterviews).map(Number));
          const maxRoundB = Math.max(0, ...Object.keys(jobBInterviews).map(Number));
          if (maxRoundA !== maxRoundB) return maxRoundB - maxRoundA;
          return a.companyName.localeCompare(b.companyName);
        }
        case 'company':
          return a.companyName.localeCompare(b.companyName);
        case 'position':
          return a.position.localeCompare(b.position);
        case 'created': {
          const dateA = new Date(a.createdAt || a.created_at);
          const dateB = new Date(b.createdAt || b.created_at);
          return dateB - dateA;
        }
        default:
          return 0;
      }
    });

    return filtered;
  };

  const jobsWithInterviews = getJobsByTab(activeTab);

  // True only when the interview flagged as final round at scheduling has
  // been converted. Jobs without any final-round flag never reach "offer awaiting".
  const isFinalFlagTrue = (v) =>
    v === true || v === 'true' || v === 1 || v === '1';
  const hasConvertedFinalRound = (jobId) => {
    const list = Object.values(interviewsByJob[jobId] || {});
    return list.some((i) => isFinalFlagTrue(i.isFinalRound) && i.status === 'converted');
  };

  // Get counts for each tab
  const tabCounts = {
    current: jobs.filter((job) => {
      const hasInterviews = interviewsByJob[job.id];
      if (!hasInterviews) return false;
      if (job.status === 'completed' || job.status === 'rejected') return false;
      if (job.status === 'waiting') return true;
      // Otherwise include only if the job is in true "offer awaiting" state
      return hasConvertedFinalRound(job.id);
    }).length,
    successful: jobs.filter(
      (job) => job.status === 'completed' && interviewsByJob[job.id]
    ).length,
    rejected: jobs.filter(
      (job) => job.status === 'rejected' && interviewsByJob[job.id]
    ).length,
  };

  // Summary KPIs
  const summary = useMemo(() => {
    const total = interviews.length;
    const waiting = interviews.filter((i) => i.status === 'waiting').length;
    const converted = interviews.filter((i) => i.status === 'converted').length;
    const rejected = interviews.filter((i) => i.status === 'rejected').length;
    const completedInterviews = total - waiting;
    const ratio = completedInterviews === 0 ? 0 : (converted / completedInterviews) * 100;
    return { total, waiting, converted, rejected, ratio };
  }, [interviews]);

  // Draw connecting lines (softer, less visually loud than before)
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d');
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    jobsWithInterviews.forEach((job, jobIndex) => {
      const jobInterviews = interviewsByJob[job.id];
      const jobRounds = Object.keys(jobInterviews).map(Number).sort((a, b) => a - b);
      if (jobRounds.length < 2) return;

      const color = getJobColor(jobIndex);
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.45;
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 6]);

      for (let i = 0; i < jobRounds.length - 1; i++) {
        const currentRound = jobRounds[i];
        const nextRound = jobRounds[i + 1];
        const currentCard = document.querySelector(
          `[data-job-round="${job.id}-${currentRound}"]`
        );
        const nextCard = document.querySelector(
          `[data-job-round="${job.id}-${nextRound}"]`
        );
        if (!currentCard || !nextCard) continue;

        const currentRect = currentCard.getBoundingClientRect();
        const nextRect = nextCard.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const startX = currentRect.right - containerRect.left;
        const startY = currentRect.top + currentRect.height / 2 - containerRect.top;
        const endX = nextRect.left - containerRect.left;
        const endY = nextRect.top + nextRect.height / 2 - containerRect.top;

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();

        // Arrow head (solid)
        const angle = Math.atan2(endY - startY, endX - startX);
        const arrowLen = 9;
        ctx.globalAlpha = 0.7;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - arrowLen * Math.cos(angle - Math.PI / 6),
          endY - arrowLen * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(endX, endY);
        ctx.lineTo(
          endX - arrowLen * Math.cos(angle + Math.PI / 6),
          endY - arrowLen * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();
        ctx.globalAlpha = 0.45;
        ctx.setLineDash([6, 6]);
      }
    });

    ctx.globalAlpha = 1;
    ctx.setLineDash([]);
  }, [interviews, jobsWithInterviews, interviewsByJob]);

  // Shared grid template columns string
  const gridTemplate = `220px repeat(${dynamicRounds.length + 1}, minmax(180px, 1fr))`;
  const kpiRoundCountVar = { '--kanban-round-count': dynamicRounds.length + 1 };

  /* ---- Render ---- */

  return (
    <div className="kanban-page" style={kpiRoundCountVar}>
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-text">
          <h1>Interviews Board</h1>
          <p>Track every job across interview rounds and outcomes.</p>
        </div>
        <div className="page-header-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/schedule')}
          >
            Schedule Interview
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="kanban-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'current'}
          className={`kanban-tab ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current leads
          <span className="kanban-tab__count">{tabCounts.current}</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'successful'}
          className={`kanban-tab ${activeTab === 'successful' ? 'active' : ''}`}
          onClick={() => setActiveTab('successful')}
        >
          Successful
          <span className="kanban-tab__count">{tabCounts.successful}</span>
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'rejected'}
          className={`kanban-tab ${activeTab === 'rejected' ? 'active' : ''}`}
          onClick={() => setActiveTab('rejected')}
        >
          Rejected
          <span className="kanban-tab__count">{tabCounts.rejected}</span>
        </button>
      </div>

      {/* Summary KPIs */}
      <div className="kanban-kpi-grid">
        <div className="kanban-kpi kanban-kpi--total">
          <p className="kanban-kpi__label">Total interviews</p>
          <p className="kanban-kpi__value">{summary.total}</p>
        </div>
        <div className="kanban-kpi kanban-kpi--waiting">
          <p className="kanban-kpi__label">Waiting</p>
          <p className="kanban-kpi__value">{summary.waiting}</p>
        </div>
        <div className="kanban-kpi kanban-kpi--converted">
          <p className="kanban-kpi__label">Converted</p>
          <p className="kanban-kpi__value">{summary.converted}</p>
        </div>
        <div className="kanban-kpi kanban-kpi--rejected">
          <p className="kanban-kpi__label">Rejected</p>
          <p className="kanban-kpi__value">{summary.rejected}</p>
        </div>
        <div className="kanban-kpi kanban-kpi--ratio">
          <p className="kanban-kpi__label">Conversion ratio</p>
          <p className="kanban-kpi__value">
            {summary.ratio.toFixed(1)}
            <span style={{ fontSize: 'var(--fs-xl)', color: 'var(--text-muted)' }}>%</span>
          </p>
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="kanban-toolbar">
        <div className="form-group form-group--search">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search by company or position…"
            value={filters.searchTerm}
            onChange={(e) => updateFilter('searchTerm', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>BD</label>
          <select
            value={filters.bdName}
            onChange={(e) => updateFilter('bdName', e.target.value)}
            disabled={loadingBds}
          >
            <option value="">{loadingBds ? 'Loading…' : 'All BDs'}</option>
            {bdOptions.map((bd) => (
              <option key={bd} value={bd}>{bd}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            <option value="">All statuses</option>
            {Object.entries(JOB_STATUSES).map(([key, value]) => (
              <option key={key} value={value}>
                {key.charAt(0) + key.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Sort by</label>
          <select
            value={filters.sortBy}
            onChange={(e) => updateFilter('sortBy', e.target.value)}
          >
            <option value="maxRound">Max round (high → low)</option>
            <option value="company">Company (A–Z)</option>
            <option value="position">Position (A–Z)</option>
            <option value="created">Created (newest)</option>
          </select>
        </div>
        <div className="kanban-toolbar__actions">
          <button className="btn btn-secondary btn-sm" onClick={clearFilters}>
            Clear
          </button>
        </div>
        {hasActiveFilters() && (
          <div className="kanban-filter-info">
            Showing {jobsWithInterviews.length} of{' '}
            {jobs.filter((job) => interviewsByJob[job.id]).length} jobs
          </div>
        )}
      </div>

      {/* Board */}
      {loading ? (
        <SkeletonKanban />
      ) : jobsWithInterviews.length === 0 ? (
        <div className="kanban-empty-state">
          <h3>No jobs to display</h3>
          <p>
            {hasActiveFilters()
              ? 'Try clearing the filters or switching tabs.'
              : 'No jobs with interviews in this category yet.'}
          </p>
        </div>
      ) : (
        <div className="kanban-board-wrapper">
          <div className="kanban-headers" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="kanban-header-job">Job</div>
            {dynamicRounds.map((round) => {
              const isFinalRoundForAnyJob = jobsWithInterviews.some((job) => {
                const interview = interviewsByJob[job.id]?.[round];
                return (
                  interview?.isFinalRound === true ||
                  interview?.isFinalRound === 'true'
                );
              });
              return (
                <div
                  key={round}
                  className={`kanban-header-round ${
                    isFinalRoundForAnyJob ? 'kanban-header-round--final' : ''
                  }`}
                >
                  Round {round}
                  {isFinalRoundForAnyJob && (
                    <span className="kanban-header-round__badge">Final</span>
                  )}
                </div>
              );
            })}
            <div className="kanban-header-round kanban-header-round--offer">
              Offer Awaiting
            </div>
          </div>

          <div
            ref={containerRef}
            className="kanban-container"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <canvas ref={canvasRef} className="kanban-canvas" />

            {jobsWithInterviews.map((job, jobIndex) => {
              const jobAccent = getJobColor(jobIndex);
              const jobStyle = { '--job-accent': jobAccent };

              // Offer awaiting: ONLY when the round flagged as final at scheduling
              // has been converted. No "all rounds converted" fallback.
              const isOfferAwaiting = hasConvertedFinalRound(job.id);

              // Unique interview takers across the job's rounds (ordered)
              const jobTakers = Array.from(
                new Set(
                  Object.values(interviewsByJob[job.id] || {})
                    .map((i) => (i.takenBy || '').trim())
                    .filter(Boolean)
                )
              );

              return (
                <div
                  key={job.id}
                  className="kanban-job-row"
                  style={{ ...jobStyle, gridTemplateColumns: gridTemplate }}
                >
                  {/* Job info column */}
                  <div
                    className={`kanban-job-info ${
                      isOfferAwaiting ? 'kanban-job-info--offer' : ''
                    }`}
                    onClick={() => setViewJobDetails(job)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setViewJobDetails(job);
                      }
                    }}
                  >
                    {isOfferAwaiting && (
                      <span className="kanban-offer-badge" title="Offer awaiting">
                        Offer
                      </span>
                    )}
                    <h5 className="kanban-job-title">{job.companyName}</h5>
                    <p className="kanban-job-position">{job.position}</p>
                    {jobTakers.length > 0 && (
                      <p
                        className="kanban-job-takers"
                        title={jobTakers.join(', ')}
                      >
                        Takers · {jobTakers.slice(0, 2).join(', ')}
                        {jobTakers.length > 2 && ` +${jobTakers.length - 2}`}
                      </p>
                    )}
                    <p className="kanban-job-bd">BD · {job.bdName}</p>
                    <span className="kanban-job-status">
                      <StatusPill status={job.status} />
                    </span>
                  </div>

                  {/* Round columns */}
                  {dynamicRounds.map((round) => {
                    const interview = interviewsByJob[job.id]?.[round];
                    if (!interview) {
                      return (
                        <div key={round} className="kanban-round-column">
                          <div className="kanban-empty-slot" />
                        </div>
                      );
                    }
                    const isFinal =
                      interview.isFinalRound === true ||
                      interview.isFinalRound === 'true';

                    return (
                      <div key={round} className="kanban-round-column">
                        <div
                          data-job-round={`${job.id}-${round}`}
                          className={`kanban-interview-card ${
                            isFinal ? 'kanban-interview-card--final' : ''
                          }`}
                          onClick={() => {
                            setSelectedInterview(interview);
                            setSelectedJob(job);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectedInterview(interview);
                              setSelectedJob(job);
                            }
                          }}
                        >
                          <div className="kanban-interview-card__header">
                            <span className="kanban-interview-card__date">
                              {fmt(interview.dateTime, 'MMM dd')}
                            </span>
                            <span className="kanban-interview-card__time">
                              {fmt(interview.dateTime, 'hh:mm a')}
                            </span>
                          </div>
                          <p className="kanban-interview-card__type">
                            {isFinal ? 'Final round' : interview.interviewType}
                          </p>
                          {interview.takenBy && (
                            <p className="kanban-interview-card__taker">
                              <strong>Taken by:</strong> {interview.takenBy}
                            </p>
                          )}
                          {interview.recruiterName && (
                            <p className="kanban-interview-card__recruiter">
                              {interview.recruiterName}
                            </p>
                          )}
                          <div className="kanban-interview-card__footer">
                            <StatusPill status={interview.status} />
                            {interview.feedback && (
                              <span
                                className="kanban-feedback-dot"
                                title="Feedback available"
                              >
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Offer Awaiting column */}
                  <div className="kanban-round-column">
                    {isOfferAwaiting ? (
                      <div
                        className="kanban-interview-card kanban-interview-card--offer"
                        onClick={() => {
                          setSelectedJob(job);
                          setSelectedInterview({
                            id: `offer-${job.id}`,
                            round: 'Offer',
                            interviewType: 'Offer Awaiting',
                            status: job.status,
                            isOffer: true,
                          });
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="kanban-interview-card__header">
                          <span className="kanban-interview-card__date">
                            Final decision
                          </span>
                        </div>
                        <p className="kanban-interview-card__type">
                          Offer awaiting
                        </p>
                        <div className="kanban-interview-card__footer">
                          <StatusPill status={job.status} />
                        </div>
                      </div>
                    ) : (
                      <div className="kanban-empty-slot" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Job details modal (full JobDetails component) */}
      {viewJobDetails && (
        <JobDetails
          job={viewJobDetails}
          onClose={() => setViewJobDetails(null)}
        />
      )}

      {/* Interview details modal */}
      {selectedInterview && selectedJob && (
        <div
          className="modal-overlay"
          onClick={() => {
            setSelectedInterview(null);
            setSelectedJob(null);
          }}
        >
          <div
            className="kanban-modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{
              '--job-accent': getJobColor(
                jobsWithInterviews.findIndex((j) => j.id === selectedJob.id) >= 0
                  ? jobsWithInterviews.findIndex((j) => j.id === selectedJob.id)
                  : 0
              ),
            }}
          >
            <div className="kanban-modal-header">
              <h2>
                {selectedInterview.isOffer
                  ? 'Offer Awaiting'
                  : `Round ${selectedInterview.round} · ${selectedInterview.interviewType}`}
              </h2>
              <button
                className="kanban-modal-close"
                onClick={() => {
                  setSelectedInterview(null);
                  setSelectedJob(null);
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="kanban-modal-body">
              {/* Job lane */}
              <div className="kanban-modal-joblane">
                <h4>
                  {selectedJob.companyName} · {selectedJob.position}
                </h4>
                <p>
                  BD: {selectedJob.bdName} · {selectedJob.jobType}
                </p>
              </div>

              {/* Interview details */}
              {!selectedInterview.isOffer && (
                <div className="kanban-modal-grid">
                  <div className="kanban-modal-item">
                    <span className="kanban-modal-item__label">Date & time</span>
                    <span className="kanban-modal-item__value">
                      {fmt(selectedInterview.dateTime, 'MMM dd, yyyy')} ·{' '}
                      {fmt(selectedInterview.dateTime, 'hh:mm a')}
                    </span>
                  </div>
                  <div className="kanban-modal-item">
                    <span className="kanban-modal-item__label">Duration</span>
                    <span className="kanban-modal-item__value">
                      {selectedInterview.duration
                        ? `${selectedInterview.duration} min`
                        : '—'}
                    </span>
                  </div>
                  {selectedInterview.recruiterName && (
                    <div className="kanban-modal-item kanban-modal-item--full">
                      <span className="kanban-modal-item__label">Recruiter</span>
                      <span className="kanban-modal-item__value">
                        {selectedInterview.recruiterName}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedInterview.isOffer && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Awaiting final decision after completing all interview rounds.
                </p>
              )}

              {/* Status changer */}
              <div className="kanban-modal-status">
                <span
                  className="kanban-modal-item__label"
                  style={{ display: 'block', marginBottom: '4px' }}
                >
                  Status
                </span>
                <select
                  value={selectedInterview.status || 'waiting'}
                  onChange={(e) => {
                    if (selectedInterview.isOffer) {
                      updateJob(selectedJob.id, { status: e.target.value });
                      setSelectedJob({ ...selectedJob, status: e.target.value });
                    } else {
                      updateInterview(selectedInterview.id, {
                        status: e.target.value,
                      });
                      if (e.target.value === 'rejected') {
                        updateJob(selectedJob.id, { status: 'rejected' });
                        setSelectedJob({ ...selectedJob, status: 'rejected' });
                      }
                    }
                    setSelectedInterview({
                      ...selectedInterview,
                      status: e.target.value,
                    });
                  }}
                >
                  {selectedInterview.isOffer ? (
                    <>
                      <option value="waiting">Waiting</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                    </>
                  ) : (
                    <>
                      <option value="waiting">Waiting</option>
                      <option value="converted">Converted</option>
                      <option value="rejected">Rejected</option>
                    </>
                  )}
                </select>
              </div>

              {/* Feedback */}
              {!selectedInterview.isOffer && (
                <div
                  className={`kanban-modal-feedback ${
                    selectedInterview.feedback
                      ? 'kanban-modal-feedback--ok'
                      : 'kanban-modal-feedback--pending'
                  }`}
                >
                  {selectedInterview.feedback ? (
                    <>
                      <div className="kanban-modal-feedback__title">
                        ✓ Feedback recorded
                      </div>
                      <div className="kanban-modal-feedback__by">
                        By {selectedInterview.takenBy || 'Unknown'}
                      </div>
                      <div className="kanban-modal-feedback__body">
                        {selectedInterview.feedback}
                      </div>
                    </>
                  ) : (
                    '⏳ Feedback pending'
                  )}
                </div>
              )}

              {/* Notes */}
              {selectedInterview.notes && (
                <div className="kanban-modal-notes">
                  <p className="kanban-modal-notes__label">Notes</p>
                  <p>{selectedInterview.notes}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="kanban-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setSelectedInterview(null);
                  setSelectedJob(null);
                }}
              >
                Close
              </button>
              {!selectedInterview.isOffer && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    navigate(
                      `/schedule?jobId=${selectedJob.id}&editInterviewId=${selectedInterview.id}`
                    );
                    setSelectedInterview(null);
                    setSelectedJob(null);
                  }}
                >
                  Edit interview
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Kanban;
